import type { GeneratedOfferingData } from './OfferingsPage';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import { useApi } from '@shared/services/api';
import { commonButtons } from '@shared/theme/patterns';
import { log } from '@shared/utils/log';
import React, { useContext, useState } from 'react';
import { UserContext } from '@/shared/contexts/UserContext';

interface AutoGenerateOfferingModalProps {
  onClose: () => void;
  onSuccess: (generatedOffering: GeneratedOfferingData) => void;
  open: boolean;
}

const AutoGenerateOfferingModal: React.FC<AutoGenerateOfferingModalProps> = ({ open, onClose, onSuccess }) => {
  const api = useApi();
  const { user } = useContext(UserContext);
  const accountId = user?.accountMemberships?.[0]?.accountId;

  const [websiteUrl, setWebsiteUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleClose = () => {
    if (!loading) {
      setWebsiteUrl('');
      setError(null);
      onClose();
    }
  };

  const handleSubmit = async () => {
    if (!websiteUrl.trim() || !accountId) {
      return;
    }

    // Basic URL validation
    let validatedUrl: URL;
    try {
      // Add https:// if no protocol is specified
      const urlToValidate = /^https?:\/\//.test(websiteUrl) ? websiteUrl : `https://${websiteUrl}`;
      validatedUrl = new URL(urlToValidate);
    } catch {
      setError('Please enter a valid URL (e.g., https://example.com)');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await api.post(`/account/${accountId}/offerings/generate`, {
        websiteUrl: validatedUrl.toString(),
      });

      if (response.error) {
        setError(response.message || 'Failed to generate offering');
        return;
      }

      // Pass the generated offering data back
      onSuccess(response.data.offering);
      handleClose();
    } catch (err) {
      log.error('Error generating offering:', err);
      setError('Failed to generate offering. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !loading && websiteUrl.trim()) {
      handleSubmit();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={!loading ? handleClose : undefined}
      maxWidth="sm"
      fullWidth
      data-testid="auto-generate-offering-modal"
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <AutoAwesomeIcon color="primary" />
        Auto-Generate Offering from Website
      </DialogTitle>

      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Enter your company's website URL and our AI will analyze it to automatically fill in your offering details.
          You can review and edit the generated content before saving.
        </Typography>

        <TextField
          data-testid="website-url-input"
          fullWidth
          label="Website URL"
          placeholder="https://yourcompany.com"
          value={websiteUrl}
          onChange={(e) => setWebsiteUrl(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={loading}
          error={!!error}
          helperText={error || "Enter the URL of your company's main website or product page"}
          autoFocus
        />

        {loading && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              mt: 3,
              p: 2,
              bgcolor: 'action.hover',
              borderRadius: 1,
            }}
          >
            <CircularProgress size={24} />
            <Box>
              <Typography variant="body2" fontWeight="medium">
                Analyzing website...
              </Typography>
              <Typography variant="caption" color="text.secondary">
                This may take 15-30 seconds
              </Typography>
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={handleClose} disabled={loading} sx={commonButtons.secondary}>
          Cancel
        </Button>
        <Button
          data-testid="generate-offering-button"
          onClick={handleSubmit}
          disabled={!websiteUrl.trim() || loading}
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <AutoAwesomeIcon />}
          sx={commonButtons.primary}
        >
          {loading ? 'Generating...' : 'Generate Offering'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AutoGenerateOfferingModal;
